import { defineStore } from 'pinia';
import { ref } from 'vue';

export type DialogType = 'info' | 'warning' | 'error' | 'success' | 'question';

export interface DialogOptions {
  title: string;
  message: string;
  type?: DialogType;
  confirmText?: string;
  cancelText?: string;
  persistent?: boolean;
  width?: string | number;
  timeout?: number; // 타임아웃 (밀리초, 0이면 자동 닫힘 없음)
}

export const useDialogStore = defineStore('dialog', () => {
  const show = ref(false);
  const title = ref('');
  const message = ref('');
  const type = ref<DialogType>('info');
  const confirmText = ref('확인');
  const cancelText = ref('취소');
  const persistent = ref(false);
  const width = ref<string | number>('500px');
  const timeout = ref<number>(0); // 0이면 타임아웃 없음
  
  let resolveCallback: ((value: boolean) => void) | null = null;
  let timeoutTimer: ReturnType<typeof setTimeout> | null = null;

  const clearTimer = () => {
    if (timeoutTimer) {
      window.clearTimeout(timeoutTimer);
      timeoutTimer = null;
    }
  };

  const showDialog = (options: DialogOptions): Promise<boolean> => {
    // 기존 타임아웃 클리어
    clearTimer();
    
    title.value = options.title;
    message.value = options.message;
    type.value = options.type || 'info';
    confirmText.value = options.confirmText || '확인';
    cancelText.value = options.cancelText || ''; // 기본값은 빈 문자열 (취소 버튼 없음)
    persistent.value = options.persistent || false;
    width.value = options.width || '500px';
    timeout.value = options.timeout || 0;
    show.value = true;

    // 타임아웃이 설정되어 있으면 자동 닫기
    if (timeout.value > 0) {
      timeoutTimer = window.setTimeout(() => {
        close();
      }, timeout.value);
    }

    return new Promise<boolean>((resolve) => {
      resolveCallback = resolve;
    });
  };

  const confirm = () => {
    clearTimer();
    if (resolveCallback) {
      resolveCallback(true);
      resolveCallback = null;
    }
    show.value = false;
  };

  const cancel = () => {
    clearTimer();
    if (resolveCallback) {
      resolveCallback(false);
      resolveCallback = null;
    }
    show.value = false;
  };

  const close = () => {
    clearTimer();
    if (!persistent.value) {
      if (resolveCallback) {
        resolveCallback(false);
        resolveCallback = null;
      }
      show.value = false;
    }
  };

  return {
    show,
    title,
    message,
    type,
    confirmText,
    cancelText,
    persistent,
    width,
    timeout,
    showDialog,
    confirm,
    cancel,
    close,
  };
});
